import { Link } from "@tanstack/react-router";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { Reveal } from "@/components/site/wow/Reveal";
import { ArrowRight, Check } from "lucide-react";
import { aeoGeo } from "@/lib/seo/schema";

export interface EnFormatPageProps {
  eyebrow: string;
  heading: string;
  /** Indices of heading words to italicise as a highlight. */
  highlightIndices?: number[];
  intro: string;
  whatItIs: {
    title: string;
    body: string;
  };
  whenToUse: string[];
  whatsIncluded: string[];
  outcomes: { metric: string; body: string }[];
  faq?: { q: string; a: string }[];
  cta?: {
    primary?: { label: string; to: string };
    secondary?: { label: string; to: string };
  };
}

const DEFAULT_CTA = {
  primary: { label: "Book a strategy call", to: "/en/contact" },
  secondary: { label: "See pricing", to: "/en/pricing" },
};

export function EnFormatPage(props: EnFormatPageProps) {
  const cta = { ...DEFAULT_CTA, ...(props.cta ?? {}) };
  return (
    <div>
      <section className="relative overflow-hidden pt-32 pb-12 md:pt-44 md:pb-20">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_80%_10%,color-mix(in_oklab,var(--mint)_10%,transparent),transparent_70%)]"
        />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="mb-6 text-xs uppercase tracking-[0.22em] text-butter">
            {props.eyebrow}
          </div>
          <KineticHeading
            text={props.heading}
            className="max-w-[22ch] text-balance text-5xl font-semibold leading-[0.98] text-foreground md:text-7xl"
            highlight={
              props.highlightIndices
                ? {
                    indices: props.highlightIndices,
                    className: "text-mint italic font-serif font-normal",
                  }
                : undefined
            }
          />
          <p
            data-quick-answer
            data-speakable
            className="lede mt-10 max-w-2xl text-pretty text-lg text-muted-foreground md:text-xl"
          >
            {props.intro}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Magnetic strength={0.3}>
              <Link
                to={cta.primary!.to}
                className="group inline-flex items-center gap-3 rounded-full bg-mint px-8 py-4 text-base font-semibold text-mint-foreground"
              >
                {cta.primary!.label}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Magnetic>
            <Link
              to={cta.secondary!.to}
              className="inline-flex items-center gap-2 text-sm text-foreground/80 underline-offset-4 hover:text-butter hover:underline"
            >
              {cta.secondary!.label} →
            </Link>
          </div>
        </div>
      </section>

      <Reveal>
        <section className="py-16 md:py-24">
          <div className="container-wide grid gap-12 md:grid-cols-2 md:items-start">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-butter">
                What it is
              </div>
              <h2 className="mt-4 text-balance text-3xl font-semibold leading-tight md:text-5xl">
                {props.whatItIs.title}
              </h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">
                {props.whatItIs.body}
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-surface p-8">
              <h3 className="text-xl font-semibold">When to use it</h3>
              <ul className="mt-6 space-y-3">
                {props.whenToUse.map((item) => (
                  <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section className="py-16 md:py-24 border-t border-white/10">
          <div className="container-wide">
            <h2 className="text-balance text-3xl font-semibold leading-tight md:text-5xl">
              What's included
            </h2>
            <div className="mt-12 grid gap-3 md:grid-cols-2">
              {props.whatsIncluded.map((item) => (
                <div
                  key={item}
                  className="flex gap-3 rounded-2xl border border-white/10 bg-surface px-5 py-4 text-sm text-foreground/90"
                >
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section className="py-16 md:py-24 border-t border-white/10">
          <div className="container-wide">
            <h2 className="text-balance text-3xl font-semibold leading-tight md:text-5xl">
              What you can expect
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {props.outcomes.map((o) => (
                <div
                  key={o.metric}
                  className="rounded-3xl border border-white/10 bg-surface p-8"
                >
                  <div className="text-5xl font-semibold text-mint">{o.metric}</div>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {o.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </Reveal>

      {props.faq && props.faq.length > 0 && (
        <Reveal>
          <section className="py-16 md:py-24 border-t border-white/10">
            <div className="container-wide max-w-4xl">
              <h2 className="text-balance text-3xl font-semibold leading-tight md:text-5xl">
                Frequently asked
              </h2>
              <div className="mt-10 space-y-6">
                {props.faq.map((f) => (
                  <div
                    key={f.q}
                    className="rounded-2xl border border-white/10 bg-surface p-6"
                  >
                    <h3 className="text-lg font-semibold">{f.q}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {f.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>
      )}

      <Reveal>
        <section className="py-24 md:py-32 border-t border-white/10">
          <div className="container-wide text-center">
            <h2 className="mx-auto max-w-3xl text-balance text-4xl font-semibold leading-tight md:text-6xl">
              Ready to make this video?
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Magnetic strength={0.3}>
                <Link
                  to={cta.primary!.to}
                  className="group inline-flex items-center gap-3 rounded-full bg-mint px-8 py-4 text-base font-semibold text-mint-foreground"
                >
                  {cta.primary!.label}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Magnetic>
              <Link
                to={cta.secondary!.to}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-medium text-foreground/90 hover:bg-white/5"
              >
                {cta.secondary!.label}
              </Link>
            </div>
          </div>
        </section>
      </Reveal>
    </div>
  );
}

export interface EnFormatHeadInput {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  enPath: string;
  svPath: string;
  jsonLd?: Record<string, unknown>;
}

/** Standard head() options for an English format page. */
export function buildEnFormatHead(input: EnFormatHeadInput) {
  const SITE = "https://fenny.se";
  const abs = (p: string) => (p.startsWith("http") ? p : `${SITE}${p}`);
  const enAbs = abs(input.enPath);
  const svAbs = abs(input.svPath);
  return {
    meta: [
      { title: input.title },
      { name: "description", content: input.description },
      { property: "og:title", content: input.ogTitle ?? input.title },
      { property: "og:description", content: input.ogDescription ?? input.description },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:url", content: enAbs },
    ],
    links: [
      { rel: "canonical", href: enAbs },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          aeoGeo({
            path: input.enPath,
            name: input.title,
            description: input.description,
            inLanguage: "en",
          }),
        ),
      },
      ...(input.jsonLd
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(input.jsonLd),
            },
          ]
        : []),
    ],
  };
}
